import { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { jwtDecode } from 'jwt-decode';
import TokenService from '../services/tokenService';
import { logoutUser } from '../api/api';

const AuthContext = createContext();

const isTokenExpired = (token) => {
  if (!token) return true;
  const decodedToken = jwtDecode(token);
  const currentTime = Date.now() / 1000; 
  return decodedToken.exp < currentTime;
};

const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
  const [loading, setLoading] = useState(true); 

  useEffect(() => {
    const initAuth = async () => {
      const token = TokenService.getAuthToken();
      if (token) {
        try {
          const isExpired = isTokenExpired(token);
          if (!isExpired) {
            const user = fetchUserDetails(token);
            setUserDetails(user);
            setIsAuthenticated(true);
            setPasswordChangeRequired(user?.passwordChangeRequired || false);
          } else {
            TokenService.clearAll();
            setIsAuthenticated(false);
            setUserDetails(null);
            setPasswordChangeRequired(false);
          }
        } catch (error) {
          console.error('Auth initialization error:', error);
          TokenService.clearAll();
          setIsAuthenticated(false);
          setUserDetails(null);
          setPasswordChangeRequired(false);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = (token, user) => {
    TokenService.setAuthToken(token);
    if (user?.tenantName) {
      TokenService.setTenantName(user.tenantName);
    }
    if (user?.tenantId) {
      TokenService.setTenantId(user.tenantId);
    }
    setUserDetails(user);
    setIsAuthenticated(true);
    setPasswordChangeRequired(user?.passwordChangeRequired || false);
  };

  const logout = async () => {
    try {
      const logoutSuccess = await logoutUser();
      if (logoutSuccess) {
        TokenService.clearAll();
        // Also clear localStorage during transition period
        localStorage.removeItem('authToken');
        localStorage.removeItem('tenantName');
        setUserDetails(null);
        setIsAuthenticated(false);
        window.location = "/";
      } else {
        console.error('Failed to logout properly');
        // Still clear tokens on frontend even if backend fails
        TokenService.clearAll();
        localStorage.removeItem('authToken');
        localStorage.removeItem('tenantName');
        setUserDetails(null);
        setIsAuthenticated(false);
        window.location = "/";
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Still clear tokens on frontend in case of error
      TokenService.clearAll();
      localStorage.removeItem('authToken');
      localStorage.removeItem('tenantName');
      setUserDetails(null);
      setIsAuthenticated(false);
      window.location = "/";
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, userDetails, login, logout, passwordChangeRequired, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  return useContext(AuthContext);
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const fetchUserDetails = (token) => {
  try {
    const decodedToken = jwtDecode(token);
    return decodedToken;
  } catch (error) {
    console.error('Error decoding user details from token:', error);
    return null;
  }
};

fetchUserDetails.propTypes = {
  token: PropTypes.string.isRequired,
};

export { AuthProvider, useAuth, fetchUserDetails };